"use client";
import React, { useState, useEffect } from "react";
import {
  Type,
  Link,
  FileText,
  Image,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { InputPrimary } from "@/components/common/InputPrimary";
import { Template } from "@/types/models/template";
import { ImageUpload } from "../../ui/elements/ImageUpload";
import { PageService } from "@/services/pageService";

// Simple debounce utility for slug checking
function debounce(
  func: (pageId: string, slug: string) => Promise<void>,
  wait: number,
) {
  let timeout: NodeJS.Timeout;
  return (pageId: string, slug: string) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(pageId, slug), wait);
  };
}

interface PageSelection {
  pageId: string;
  selected: boolean;
  customData?: {
    title: string;
    slug: string;
    description: string;
    featured_image: string;
  };
}

interface PageCustomizationFormProps {
  template: Template;
  selectedPages: PageSelection[];
  uploadingImages: Set<string>;
  uploadedImages: Map<string, string>;
  onTitleChange: (pageId: string, title: string) => void;
  onUpdateCustomData: (pageId: string, field: string, value: string) => void;
  onImageSelected: (
    pageId: string,
    selectedFile: { assetId: string; url: string },
  ) => void;
  onRemoveUploadedImage: (pageId: string) => void;
  onSlugValidationChange?: (
    pageId: string,
    isValid: boolean | null,
    isChecking: boolean,
  ) => void;
  generateSlug: (title: string) => string;
}

export const PageCustomizationForm: React.FC<PageCustomizationFormProps> = ({
  template,
  selectedPages,
  uploadingImages,
  uploadedImages,
  onTitleChange,
  onUpdateCustomData,
  onImageSelected,
  onRemoveUploadedImage,
  onSlugValidationChange,
  generateSlug,
}) => {
  // State for slug validation
  const [slugValidation, setSlugValidation] = useState<
    Record<
      string,
      {
        isChecking: boolean;
        isValid: boolean | null;
        message: string;
      }
    >
  >({});

  // Debounced slug validation function
  const debouncedSlugCheck = debounce(async (pageId: string, slug: string) => {
    if (!slug || slug.trim() === "") {
      setSlugValidation((prev) => ({
        ...prev,
        [pageId]: { isChecking: false, isValid: null, message: "" },
      }));
      onSlugValidationChange?.(pageId, null, false);
      return;
    }

    setSlugValidation((prev) => ({
      ...prev,
      [pageId]: { ...prev[pageId], isChecking: true },
    }));

    // Notify parent that checking has started
    onSlugValidationChange?.(pageId, null, true);

    try {
      const result = await PageService.checkSlugAvailability(slug);
      const validationState = {
        isChecking: false,
        isValid: result.available,
        message: result.message,
      };

      setSlugValidation((prev) => ({
        ...prev,
        [pageId]: validationState,
      }));

      // Notify parent component of validation state change
      onSlugValidationChange?.(pageId, result.available, false);
    } catch (error) {
      console.error("Error checking slug availability:", error);
      const errorState = {
        isChecking: false,
        isValid: false,
        message: "Error checking slug availability",
      };

      setSlugValidation((prev) => ({
        ...prev,
        [pageId]: errorState,
      }));

      // Notify parent component of validation error
      onSlugValidationChange?.(pageId, false, false);
    }
  }, 500);

  // Handle slug change with validation
  const handleSlugChange = (pageId: string, value: string) => {
    const cleanSlug = generateSlug(value);
    onUpdateCustomData(pageId, "slug", cleanSlug);
    debouncedSlugCheck(pageId, cleanSlug);
  };

  // Initialize validation state for existing slugs
  useEffect(() => {
    selectedPages.forEach((selection) => {
      const slug = selection.customData?.slug;
      if (slug && !slugValidation[selection.pageId]) {
        debouncedSlugCheck(selection.pageId, slug);
      }
    });
  }, [selectedPages, debouncedSlugCheck, slugValidation]);

  const getSlugValidationIcon = (pageId: string) => {
    const validation = slugValidation[pageId];
    if (!validation) return null;

    if (validation.isChecking) {
      return <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />;
    }

    if (validation.isValid === true) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }

    if (validation.isValid === false) {
      return <AlertCircle className="w-4 h-4 text-destructive" />;
    }

    return null;
  };
  return (
    <div
      className="bg-card dark:bg-auth-card rounded-xl border border-border dark:border-auth-background shadow-sm mb-8 animate-fadeInUp"
      style={{ animationDelay: "0.1s" }}
    >
      <div className="p-8">
        <h3 className="text-lg font-semibold text-foreground mb-6">
          Customize Page Details
        </h3>

        {selectedPages.map((selection) => {
          const page = template.pages?.find((p) => p.id === selection.pageId);
          if (!page) return null;

          return (
            <div key={selection.pageId} className="space-y-6">
              {/* Page Title */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                  <Type className="w-4 h-4" />
                  <span>Page Title</span>
                  <span className="text-destructive">*</span>
                </label>
                <InputPrimary
                  type="text"
                  value={selection.customData?.title || ""}
                  onChange={(e) =>
                    onTitleChange(selection.pageId, e.target.value)
                  }
                  placeholder="e.g., Home, About Us, Contact"
                />
              </div>

              {/* Page Slug */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                  <Link className="w-4 h-4" />
                  <span>Page Slug (URL)</span>
                  <span className="text-destructive">*</span>
                </label>
                <div className="space-y-2">
                  <div className="flex">
                    <span className="inline-flex items-center px-3 text-sm text-muted-foreground bg-muted border border-r-0 border-border rounded-l-lg">
                      /
                    </span>
                    <div className="relative flex-1">
                      <InputPrimary
                        type="text"
                        value={selection.customData?.slug || ""}
                        onChange={(e) =>
                          handleSlugChange(selection.pageId, e.target.value)
                        }
                        placeholder="page-url"
                        className={`rounded-l-none pr-10 ${
                          slugValidation[selection.pageId]?.isValid === false
                            ? "border-destructive focus:ring-destructive focus:border-destructive"
                            : slugValidation[selection.pageId]?.isValid === true
                              ? "border-green-500 focus:ring-green-500 focus:border-green-500"
                              : ""
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {getSlugValidationIcon(selection.pageId)}
                      </div>
                    </div>
                  </div>
                  {slugValidation[selection.pageId]?.message && (
                    <p
                      className={`text-xs ${
                        slugValidation[selection.pageId]?.isValid === false
                          ? "text-destructive"
                          : "text-green-600 dark:text-green-400"
                      }`}
                    >
                      {slugValidation[selection.pageId]?.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Page Description */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                  <FileText className="w-4 h-4" />
                  <span>Page Description</span>
                </label>
                <textarea
                  value={selection.customData?.description || ""}
                  onChange={(e) =>
                    onUpdateCustomData(
                      selection.pageId,
                      "description",
                      e.target.value,
                    )
                  }
                  placeholder="Brief description of what this page is about..."
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border dark:border-auth-background rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              </div>

              {/* Featured Image Upload */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-foreground mb-2">
                  <Image className="w-4 h-4" aria-hidden="true" />
                  <span>Featured Image</span>
                </label>

                <ImageUpload
                  pageId={selection.pageId}
                  currentImage={
                    uploadedImages.get(selection.pageId) ||
                    selection.customData?.featured_image
                  }
                  isUploading={uploadingImages.has(selection.pageId)}
                  onImageSelected={onImageSelected}
                  onRemoveImage={onRemoveUploadedImage}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
